import { MutateOptions, useQueryClient } from '@tanstack/react-query';
import { Graphback, IssueMessageType, useCreateMutation } from '../../graphql';
import { NewMessage } from '../domain';
import {
  ANONYMOUS_ISSUE_MESSAGE_FRAGMENT,
  ISSUE_MESSAGE_FRAGMENT,
} from './issueMessageFragment';
import {
  extractMentionsFromText,
  replaceMentionNamesWithPlaceholder,
} from '../../utils/mentionsHelper';

export type CreateIssueMessageArgs = {
  issue: any;
  message: NewMessage;
  options?: MutateOptions<any, any, unknown, unknown>;
  /**
   * The share link's creator, supplied only when the poster is signed out and
   * replying through a share link. Stamps `guestOf` on the message and
   * switches the response selection to the id-only variant the Anonymous
   * role can read. Ignored when no share token is set.
   */
  anonymousGuestOf?: { id: string };
};

export const useCreateIssueMessage = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useCreateMutation({
    modelName: 'issueMessage',
    query: CREATE_ISSUE_MESSAGE,
  });
  const { mutateAsync: mutateAsyncAnonymous, isPending: isPendingAnonymous } =
    useCreateMutation({
      modelName: 'issueMessage',
      query: CREATE_ISSUE_MESSAGE_ANONYMOUS,
    });

  const createIssueMessage = async (args: CreateIssueMessageArgs) => {
    const mentions = extractMentionsFromText(args.message.message);

    args.message.message = replaceMentionNamesWithPlaceholder(
      args.message.message
    );

    const input: any = {
      issue: { id: args.issue.id },
      type: args.message.type ?? IssueMessageType.UserMessage,
      text: args.message.message,
      workspace: { id: args.issue.workspace.id },
      userMentions: mentions,
    };

    if (args.message.media) {
      input.media = args.message.media.map((x) => {
        return { id: x.id };
      });
    }

    // Side Chat (plan 0022): only set `parentMessage` when a parent id is
    // actually present. NEVER assign `input.parentMessage = { id: undefined }`
    // or `undefined` — graphback's relationship in-guard treats a present-but-
    // undefined key as set, then dereferences `.id` and crashes with
    // "Cannot read properties of null (reading '_bsontype')"
    // (bd memory reference_graphback_relationship_undefined_key_crash). Omitting
    // the key entirely keeps the no-parent path byte-for-byte identical.
    if (args.message.parentMessage?.id) {
      input.parentMessage = { id: args.message.parentMessage.id };
    }

    // A reply posted through a share link is the one path that can turn the
    // poster into a Guest server-side (`IssueMessageProxyService`), which
    // stands their auto-created personal workspace down so the conversation
    // they just joined lands in My History. The cached account still names
    // that workspace, and the activity and asset lists send its id
    // explicitly — so without the refetch below the two nav destinations stay
    // pointed at the workspace the guest was just moved out of until a full
    // reload.
    //
    // Read BEFORE the request, not after: the token lives on a mutable
    // singleton that `ShareView` clears when it unmounts, so a caller who
    // follows a sidebar link while the reply is still in flight would
    // otherwise skip the refetch on exactly the request that carried the
    // token and may have stood their workspace down — landing them on the
    // stale cache, which is the bug this exists to prevent.
    const isShareLinkReply = Graphback.hasShareToken();

    // Same omit-the-key rule as parentMessage above.
    const isAnonymousShareLinkReply =
      isShareLinkReply && !!args.anonymousGuestOf?.id;
    if (isAnonymousShareLinkReply) {
      input.guestOf = { id: args.anonymousGuestOf!.id };
    }

    const result = isAnonymousShareLinkReply
      ? await mutateAsyncAnonymous({ input }, args.options)
      : await mutateAsync({ input }, args.options);

    // Awaited on purpose: the caller may navigate straight into one of those
    // lists, and a fresh account before the reply resolves is what stops that
    // first request carrying the stale workspace id. It cannot fail the reply
    // — `invalidateQueries` records a failed refetch in the query's own state
    // rather than rejecting (`throwOnError` is opt-in), which the spec beside
    // this one pins.
    if (isShareLinkReply) {
      await queryClient.invalidateQueries({ queryKey: ['currentAccount'] });
    }

    return result;
  };

  return {
    createIssueMessage,
    isPending: isPending || isPendingAnonymous,
  };
};

const CREATE_ISSUE_MESSAGE = `
  mutation createIssueMessage($input: CreateIssueMessageInput!) {
    createIssueMessage(input: $input) {
      ${ISSUE_MESSAGE_FRAGMENT}
    }
  }
`;

const CREATE_ISSUE_MESSAGE_ANONYMOUS = `
  mutation createIssueMessage($input: CreateIssueMessageInput!) {
    createIssueMessage(input: $input) {
      ${ANONYMOUS_ISSUE_MESSAGE_FRAGMENT}
    }
  }
`;
